import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useBranding } from "../context/BrandingContext";
import esCL from "./es-CL";
import ptBR from "./pt-BR";
import en from "./en";

// i18n mínimo para las pantallas del alumno.
//
// - El idioma por defecto y los habilitados vienen de la organización
//   (`company.locales`); sin configurar, solo español de Chile.
// - La persona elige entre los habilitados (LanguageSwitcher); su elección
//   queda en localStorage y se respeta mientras la org la tenga habilitada.
// - `t(key, vars)` cae a es-CL y, si tampoco está, devuelve la clave: nunca
//   rompe una pantalla por una traducción que falta.

export const DEFAULT_LOCALE = "es-CL";
export const LOCALES = {
  "es-CL": { label: "Español", short: "ES", dict: esCL, lang: "es" },
  "pt-BR": { label: "Português", short: "PT", dict: ptBR, lang: "pt-BR" },
  en: { label: "English", short: "EN", dict: en, lang: "en" },
};
export const LOCALE_CODES = Object.keys(LOCALES);

const STORAGE_KEY = "ewaffle:locale";

const readStored = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) || "";
  } catch {
    return "";
  }
};

const interpolate = (text, vars) =>
  vars ? String(text).replace(/\{(\w+)\}/g, (_, k) => (vars[k] === undefined ? `{${k}}` : String(vars[k]))) : text;

export const translate = (locale, key, vars) => {
  const dict = LOCALES[locale]?.dict || {};
  const text = dict[key] ?? esCL[key] ?? key;
  return interpolate(text, vars);
};

const LocaleContext = createContext({
  locale: DEFAULT_LOCALE,
  enabled: [DEFAULT_LOCALE],
  setLocale: () => {},
  t: (key, vars) => translate(DEFAULT_LOCALE, key, vars),
});

export const LocaleProvider = ({ children }) => {
  const { org } = useBranding();
  const orgLocales = org?.locales || {};
  const enabled = useMemo(() => {
    const list = (orgLocales.enabled || []).filter((code) => LOCALES[code]);
    const def = LOCALES[orgLocales.default] ? orgLocales.default : DEFAULT_LOCALE;
    return list.length ? [...new Set([def, ...list])] : [def];
  }, [orgLocales.enabled, orgLocales.default]);
  const orgDefault = enabled[0];

  const [chosen, setChosen] = useState(readStored);

  const locale = enabled.includes(chosen) ? chosen : orgDefault;

  const setLocale = useCallback((code) => {
    if (!LOCALES[code]) return;
    setChosen(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      // sin storage, la elección dura la sesión
    }
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = LOCALES[locale]?.lang || "es";
    }
  }, [locale]);

  const t = useCallback((key, vars) => translate(locale, key, vars), [locale]);

  const value = useMemo(() => ({ locale, enabled, setLocale, t }), [locale, enabled, setLocale, t]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = () => useContext(LocaleContext);
export const useT = () => useContext(LocaleContext).t;

export default LocaleContext;
